'use client';

import { useState, useRef, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import { ApiResponse, ChatCompletionResponse, ChatMessage } from '@/lib/types/openai';
import { useLanguage } from '@/contexts/LanguageContext';

interface Message {
  id: number;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
}

export default function RapidaChatbot() {
  const { t } = useLanguage();
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 1,
      text: t('chatbot.greeting'),
      sender: 'bot',
      timestamp: new Date(),
    },
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const conversationHistory = useRef<ChatMessage[]>([
    {
      role: 'system',
      content: t('chatbot.systemPrompt'),
    },
  ]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async () => {
    if (!inputValue.trim()) return;

    const userMessage: Message = {
      id: messages.length + 1,
      text: inputValue,
      sender: 'user',
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);
    setError(null);

    conversationHistory.current.push({
      role: 'user',
      content: inputValue,
    });

    try {
      const res = await fetch('/api/openai/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          messages: conversationHistory.current,
        }),
      });

      const data: ApiResponse<ChatCompletionResponse> = await res.json();

      if (data.success) {
        const botMessage: Message = {
          id: messages.length + 2,
          text: data.data.message,
          sender: 'bot',
          timestamp: new Date(),
        };

        conversationHistory.current.push({
          role: 'assistant',
          content: data.data.message,
        });

        setMessages((prev) => [...prev, botMessage]);
      } else {
        setError(data.error.message);
        
        if (!data.error.isInternal) {
          console.error('Error sending message:', data.error.message);
        }

        const errorMessage: Message = {
          id: messages.length + 2,
          text: t('chatbot.errorProcessing'),
          sender: 'bot',
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, errorMessage]);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Network error occurred.';
      setError(errorMessage);
      console.error('Network Error:', err);

      const errorBotMessage: Message = {
        id: messages.length + 2,
        text: t('chatbot.errorConnection'),
        sender: 'bot',
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorBotMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-lg overflow-hidden flex flex-col h-[600px]">
      {/* Chat Header */}
      <div className="bg-primary text-primary-foreground px-6 py-4 flex items-center space-x-3">
        <Icon name="ChatBubbleLeftRightIcon" size={24} variant="solid" />
        <div>
          <h3 className="font-heading font-bold text-lg">Rapida Team AI Assistant</h3>
          <p className="text-xs opacity-80">{t('chatbot.poweredBy')}</p>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border-b border-red-200 px-6 py-2">
          <p className="text-xs text-red-600">{error}</p>
        </div>
      )}

      {/* Messages Container */}
      <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-muted/20">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[75%] rounded-lg px-4 py-3 ${
                message.sender === 'user' ?'bg-primary text-primary-foreground' :'bg-card border border-border text-foreground'
              }`}
            >
              <p className="text-sm leading-relaxed">{message.text}</p>
              {isMounted && (
                <p
                  className={`text-xs mt-1 ${
                    message.sender === 'user' ? 'opacity-70' : 'text-muted-foreground'
                  }`}
                >
                  {message.timestamp.toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </p>
              )}
            </div>
          </div>
        ))}

        {isTyping && (
          <div className="flex justify-start">
            <div className="bg-card border border-border rounded-lg px-4 py-3">
              <div className="flex space-x-2">
                <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="bg-card border-t border-border px-6 py-4">
        <div className="flex space-x-3">
          {isMounted ? (
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={t('chatbot.placeholder')}
              className="flex-1 px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
              disabled={isTyping}
            />
          ) : (
            <div className="flex-1 px-4 py-2 border border-border rounded-lg bg-background text-muted-foreground">
              {t('chatbot.placeholder')}
            </div>
          )}
          <button
            onClick={handleSendMessage}
            disabled={!inputValue.trim() || isTyping}
            className="px-6 py-2 bg-primary text-primary-foreground rounded-lg font-heading font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            <span>{t('common.send')}</span>
            <Icon name="PaperAirplaneIcon" size={18} variant="solid" />
          </button>
        </div>
      </div>
    </div>
  );
}